import React, { useEffect, useState } from 'react';
import { motion, Variants } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../components/common/LoadingSpinner';

interface LegalSection {
  id: number;
  section_order: number;
  section_title: string;
  section_content: string;
}

interface LegalPageData {
  id: number;
  page_type: string;
  language: string;
  title: string;
  last_updated: string;
  content: string;
  sections: LegalSection[];
}

const TermsOfService: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [pageData, setPageData] = useState<LegalPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTermsOfService = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://patrixglobal.com/backend/api/legal/terms-of-service?lang=${i18n.language}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch terms of service');
        }

        const data = await response.json();
        if (data.success) {
          setPageData(data.data);
        } else {
          throw new Error(data.message || 'Failed to load terms of service');
        }
      } catch (err) {        setError('Failed to load terms of service. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTermsOfService();
  }, [i18n.language]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">{error || 'Terms of service not found'}</p>
          <a href="/" className="text-primary-600 hover:text-primary-700 underline">
            {t('common.backToHome', 'Back to Home')}
          </a>
        </div>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-700 to-primary-900 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-primary-500 rounded-full opacity-10 animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-48 h-48 bg-primary-400 rounded-full opacity-10 animate-pulse"></div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex items-center justify-center mb-6">
              <svg
                className="w-16 h-16 mr-4 rtl:ml-4 rtl:mr-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h1 className="text-4xl md:text-5xl font-bold">
                {pageData.title}
              </h1>
            </div>
            <p className="text-xl text-primary-100">
              {t('legal.lastUpdated', 'Last Updated')}: {new Date(pageData.last_updated).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <motion.section
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Introduction */}
        <motion.div
          className="mb-12 p-8 bg-white rounded-2xl shadow-lg border border-gray-100"
          variants={itemVariants}
        >
          <p className="text-lg text-gray-700 leading-relaxed whitespace-pre-line">
            {pageData.content}
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-8">
          {pageData.sections.map((section, index) => (
            <motion.div
              key={section.id}
              className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow duration-300"
              variants={itemVariants}
            >
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-800 rounded-lg flex items-center justify-center mr-4 rtl:ml-4 rtl:mr-0">
                  <span className="text-white font-bold text-lg">{index + 1}</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mt-1">
                  {section.section_title}
                </h2>
              </div>
              <div className="ml-14 rtl:mr-14 rtl:ml-0">
                <div className="text-gray-700 leading-relaxed whitespace-pre-line text-base">
                  {section.section_content}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Important Notice */}
        <motion.div
          className="mt-12 p-6 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg"
          variants={itemVariants}
        >
          <div className="flex items-start">
            <svg
              className="w-6 h-6 text-yellow-600 mr-3 rtl:ml-3 rtl:mr-0 flex-shrink-0 mt-1"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                {t('legal.importantNotice', 'Important Notice')}
              </h3>
              <p className="text-yellow-700">
                {t('legal.termsNotice', 'By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.')}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-16 p-8 bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl text-center"
          variants={itemVariants}
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {t('legal.questionsTitle', 'Have Questions?')}
          </h3>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            {t('legal.termsQuestionsDesc', 'If you have any questions or concerns about our Terms of Service, please don\'t hesitate to contact us.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a
              href="/contact"
              className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-200 shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('common.contactUs', 'Contact Us')}
            </motion.a>
            <motion.a
              href="/"
              className="border-2 border-primary-600 text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-primary-600 hover:text-white transition-colors duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('common.backToHome', 'Back to Home')}
            </motion.a>
          </div>
        </motion.div>
      </motion.section>
    </div>
  );
};

export default TermsOfService;